import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import useSkuListing from "../../hooks/useSkuListing";
import useWork from "../../hooks/useWork";
import SkuScopeFilters from "./SkuScopeFilters";

const provinces = [
  "BC",
  "AB",
  "SK",
  "MB",
  "ON",
  "QC",
  "NB",
  "NS",
  "NL",
  "PE",
  "NT",
  "YT",
  "NU",
];

const SkuListingView = ({ sku, clientId }) => {
  const { client, listingData, loading, fetchListings } = useSkuListing();
  const { queueWork } = useWork();

  const [filters, setFilters] = useState({});

  useEffect(() => {
    console.log("clientId: ", clientId);
    if (clientId && sku?.id) {
      fetchListings(clientId, sku.id, filters);
    }
  }, [clientId, sku?.id, filters]);

  const handleExportTemplate = async () => {
    const email = prompt("Enter email address for template delivery:");
    if (!email) return;

    await queueWork({
      workerType: "exportListingsTemplate",
      details: { email, entityId: clientId },
    });

    alert("Listings template export queued. Check your email.");
  };

  if (!client) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          Loading client configuration...
        </Typography>
      </Box>
    );
  }

  if (!client.listingOptions) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          No listing options configured for this client.
        </Typography>
      </Box>
    );
  }

  const { retailLevel, countryLevel } = client.listingOptions;
  const isProvincial = countryLevel === "provincial";

  const formatLocationScope = (listing) => {
    const parts = [];
    if (listing.locationName) {
      parts.push(listing.locationName);
    }
    if (listing.locationNumber) {
      parts.push(`#${listing.locationNumber}`);
    }

    const addressParts = [
      listing.locationStreetAddress,
      listing.locationCity,
      listing.locationProvince,
      listing.locationPostal,
    ].filter(Boolean);

    if (addressParts.length > 0) {
      parts.push(addressParts.join(", "));
    }

    return parts.join(" | ") || "N/A";
  };

  const renderListingValue = (value) => {
    if (value === 1 || value === true) return "✓";
    if (value === 0 || value === false) return "";
    return "-";
  };

  const renderTable = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (listingData.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="text.secondary">
            No listings configured for this SKU. Export template to add
            listings.
          </Typography>
        </Box>
      );
    }

    return (
      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {retailLevel !== "national" && <TableCell>Scope</TableCell>}
              {isProvincial ? (
                provinces.map((p) => <TableCell key={p}>{p}</TableCell>)
              ) : (
                <TableCell>Listed</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {listingData.map((listing) => (
              <TableRow key={listing.id}>
                {retailLevel !== "national" && (
                  <TableCell>
                    {retailLevel === "location"
                      ? formatLocationScope(listing)
                      : listing.retailerName || listing.bannerName || "N/A"}
                  </TableCell>
                )}
                {isProvincial ? (
                  provinces.map((p) => (
                    <TableCell key={p}>
                      {renderListingValue(listing.listing?.listings?.[p])}
                    </TableCell>
                  ))
                ) : (
                  <TableCell>
                    {renderListingValue(listing.listing?.listings?.national)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1 }}
      >
        Client: {client.name} ({retailLevel} / {countryLevel})
      </Typography>

      <SkuScopeFilters
        retailLevel={retailLevel}
        countryLevel={countryLevel}
        clientId={clientId}
        onChange={setFilters}
      />

      {renderTable()}

      <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleExportTemplate}
        >
          Export Listings Template
        </Button>
        <Typography
          variant="caption"
          color="text.secondary"
        >
          Bulk edits via export → Excel → import
        </Typography>
      </Box>
    </Box>
  );
};

export default SkuListingView;
